import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Sparkles } from "lucide-react";

const TIERS = [
  {
    key: "free", name: "Free", price: 0,
    tagline: "For getting started.",
    features: [
      "Watchlists across all 34 commodities",
      "15-min delayed prices (Yahoo)",
      "Weekly market summary",
      "Public analyst notes + EOD one-liners",
      "USD pricing only",
    ],
  },
  {
    key: "pro", name: "Pro", price: 29,
    tagline: "Advisory buying for procurement teams.",
    highlight: true,
    features: [
      "Real-time CME data + session insights",
      "AI Buy / Hold / Wait signals (Claude Sonnet 5)",
      "Cross-commodity impact analysis (short/med/long term)",
      "Full daily EOD recaps + advisory commentary",
      "Vendor comparison with basis vs benchmark",
      "Recipe cost simulator + supplier intros",
      "Currency conversion — top 8 (EUR, GBP, JPY, CNY, INR, BRL, AUD, CAD)",
      "Unlimited custom alerts + email delivery",
    ],
  },
  {
    key: "enterprise", name: "Enterprise", price: 199,
    tagline: "For desks, mills and industrial buyers.",
    features: [
      "Everything in Pro",
      "All 19+ currencies (CHF, SGD, MYR, IDR, MXN, ZAR, TRY, KRW, THB, RUB…)",
      "Premium vendors + negotiated rates",
      "Enterprise-only analyst notes + supplier intros",
      "Hedging module (roadmap Q2)",
      "1:1 analyst chat access (roadmap)",
      "API export",
    ],
  },
];

export default function Pricing() {
  const { user, refresh } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    const status = sp.get("upgrade");
    const sessionId = sp.get("session_id");
    if (status === "success" && sessionId) {
      api.post(`/subscription/confirm/${sessionId}`).then(({data}) => {
        if (data.upgraded) { toast.success(`Upgraded to ${data.tier.toUpperCase()}`); refresh(); }
        else toast.error("Payment not completed");
      }).catch(() => toast.error("Could not confirm session"));
      setSp({}, { replace: true });
    } else if (status === "cancel") {
      toast.info("Upgrade cancelled");
      setSp({}, { replace: true });
    }
  }, []); // eslint-disable-line

  const startCheckout = async (tier) => {
    setBusy(tier);
    try {
      const { data } = await api.post("/subscription/checkout", { tier, billing: "monthly" });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Checkout failed"); setBusy(null); }
  };

  const devUpgrade = async (tier) => {
    setBusy(tier);
    try {
      await api.post("/subscription/dev-upgrade", { tier, billing: "monthly" });
      toast.success(`Upgraded to ${tier.toUpperCase()} (demo)`);
      await refresh();
    } catch (e) { toast.error("Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8">
      <div className="text-center">
        <div className="text-[10px] font-mono uppercase tracking-widest text-primary">Plans</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">Pick the tier that fits your desk.</h1>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground">
          All prices in <strong className="text-foreground">USD</strong>. Pro tier is built for procurement / advisory buying — real-time data, cascade analysis, vendor benchmarking, and 8 major currencies. Any currency outside the top 8 requires Enterprise. Payments powered by Stripe (test mode).
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {TIERS.map(t => {
          const current = user?.tier === t.key;
          return (
            <Card key={t.key} data-testid={`tier-card-${t.key}`}
              className={`relative border-border/60 bg-card/60 ${t.highlight ? "ring-2 ring-primary/60" : ""}`}>
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="rounded-full bg-primary text-primary-foreground uppercase text-[10px] tracking-widest">
                    <Sparkles className="mr-1 h-3 w-3" />Most popular
                  </Badge>
                </div>
              )}
              <CardContent className="space-y-5 p-6">
                <div>
                  <div className="font-display text-2xl font-bold">{t.name}</div>
                  <div className="text-sm text-muted-foreground">{t.tagline}</div>
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-display text-5xl font-bold">${t.price}</span>
                  <span className="text-xs font-mono text-muted-foreground">/mo</span>
                </div>
                <ul className="space-y-2 text-sm">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="space-y-2">
                  {current ? (
                    <Button disabled className="w-full rounded-full" data-testid={`tier-current-${t.key}`}>Current plan</Button>
                  ) : t.key === "free" ? (
                    <Button variant="outline" disabled className="w-full rounded-full">Included</Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => startCheckout(t.key)}
                        disabled={busy === t.key}
                        data-testid={`tier-checkout-${t.key}`}
                        className="w-full rounded-full">
                        {busy === t.key ? "Redirecting…" : `Upgrade with Stripe`}
                      </Button>
                      <Button
                        onClick={() => devUpgrade(t.key)}
                        variant="ghost"
                        size="sm"
                        data-testid={`tier-demo-${t.key}`}
                        className="w-full text-xs text-muted-foreground">
                        or activate demo (no payment)
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-center text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
        Market insight, not financial advice
      </p>
    </div>
  );
}
